import { MessageSquare, Star, ThumbsUp, ThumbsDown, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useFeedback, useFeedbackStats } from "@/hooks/useAdminData";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";

const RATING_COLORS = {
  5: '#22c55e',
  4: '#84cc16',
  3: '#eab308',
  2: '#f97316',
  1: '#ef4444',
};

const LIKED_LABELS: Record<string, string> = {
  lyrics: '📝 Текст',
  music: '🎵 Музыка',
  speed: '⚡️ Скорость',
  personalization: '🎯 Персонализация',
};

const IMPROVE_LABELS: Record<string, string> = {
  lyrics: '📝 Текст',
  music: '🎵 Музыка',
  voice: '🎤 Голос',
  length: '⏱ Длина',
};

export default function Feedback() {
  const { data: feedback, isLoading } = useFeedback();
  const { data: stats, isLoading: statsLoading } = useFeedbackStats();

  const ratingDistribution = stats?.ratingDistribution || [];
  const avgRating = stats?.avgRating || 0;
  const totalFeedback = stats?.totalFeedback || 0;

  return (
    <AdminLayout title="Обратная связь" description="Отзывы пользователей о сгенерированных хитах">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Всего отзывов</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{totalFeedback}</div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Средняя оценка</CardTitle>
            <Star className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">
                {avgRating.toFixed(1)} <span className="text-sm text-muted-foreground">/ 5</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Довольных (4-5⭐)</CardTitle>
            <ThumbsUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-green-500">
                {stats?.satisfiedPercent || 0}%
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Недовольных (1-2⭐)</CardTitle>
            <ThumbsDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-red-500">
                {stats?.unsatisfiedPercent || 0}%
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Распределение оценок
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={ratingDistribution}>
                  <XAxis dataKey="rating" tickFormatter={(v) => `${v}⭐`} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number) => [`${value} отзывов`, 'Количество']}
                    labelFormatter={(label) => `Оценка: ${label}⭐`}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="hsl(var(--primary))"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Что улучшить (топ)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : stats?.improveStats && stats.improveStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={stats.improveStats}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    dataKey="count"
                    nameKey="item"
                    label={({ item, count }) => `${IMPROVE_LABELS[item] || item}: ${count}`}
                  >
                    {stats.improveStats.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground text-center py-8">Нет данных</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Feedback */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Последние отзывы
          </CardTitle>
          <CardDescription>{feedback?.length || 0} отзывов</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : !feedback?.length ? (
            <p className="text-muted-foreground text-center py-8">
              Пока нет отзывов
            </p>
          ) : (
            <div className="space-y-3">
              {feedback.map((fb) => (
                <div key={fb.id} className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div 
                        className="flex items-center gap-1 px-2 py-1 rounded text-white font-medium text-sm"
                        style={{ backgroundColor: RATING_COLORS[fb.rating as keyof typeof RATING_COLORS] || '#888' }}
                      >
                        <Star className="w-3 h-3" />
                        {fb.rating}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {fb.telegram_users?.username ? `@${fb.telegram_users.username}` : fb.telegram_users?.first_name || 'Пользователь'}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(fb.created_at), "d MMM HH:mm", { locale: ru })}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {fb.liked?.map((item) => (
                      <Badge key={item} variant="outline" className="text-green-600 border-green-600/30 bg-green-50 dark:bg-green-950/20">
                        👍 {LIKED_LABELS[item] || item}
                      </Badge>
                    ))}
                    {fb.improve?.map((item) => (
                      <Badge key={item} variant="outline" className="text-orange-600 border-orange-600/30 bg-orange-50 dark:bg-orange-950/20">
                        💡 {IMPROVE_LABELS[item] || item}
                      </Badge>
                    ))}
                  </div>
                  
                  {fb.comment && (
                    <p className="mt-2 text-sm italic text-muted-foreground">
                      "{fb.comment}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
